import { useCallback, useContext } from 'react';
import clsx from 'clsx';
import debounce from 'lodash.debounce';

import WorkOrderDatePicker from '@atom/components/common/workOrderDetail/workOrderFields/WorkOrderDatePicker';
import WorkOrderEnumMultipleField from '@atom/components/common/workOrderDetail/workOrderFields/WorkOrderEnumMultipleField';
import WorkOrderEnumSingleField from '@atom/components/common/workOrderDetail/workOrderFields/WorkOrderEnumSingleField';
import WorkOrderNumericField from '@atom/components/common/workOrderDetail/workOrderFields/WorkOrderNumericField';
import WorkOrderSummary from '@atom/components/common/workOrderDetail/workOrderFields/WorkOrderSummary';
import WorkOrderTextField from '@atom/components/common/workOrderDetail/workOrderFields/WorkOrderTextField';
import { DataType } from '@atom/types/dataType';
import { WorkOrderField } from '@atom/types/work';
import {
  doesNotHaveRolePermissions,
  hasRolePermissions,
  ROLE_SETS,
} from '@atom/utilities/authUtilities';
import { CustomFieldView } from '@atom/utilities/taskUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import BulkContext from '../../../BulkContext';

import classes from './workInfo.module.css';

const DEBOUNCE_TIME = 1000;

const CustomFields = () => {
  const { workOrder, setWorkOrder } = useContext(BulkContext);
  const { fieldOrder, fields } = workOrder;

  const updateFields = (newFields: WorkOrderField[]) => {
    const newWorkOrder = {
      ...workOrder,
      fields: [...newFields],
    };

    setWorkOrder(newWorkOrder);
  };

  const handleChange = (fieldId: string, value: any) => {
    const updatedFields = fields.map((field: WorkOrderField) =>
      field.id === fieldId ? { ...field, value } : field,
    );
    updateFields(updatedFields);
  };

  const handleChangeDebounced = useCallback(
    debounce(handleChange, DEBOUNCE_TIME),
    [handleChange],
  );

  const showButton = hasRolePermissions(ROLE_SETS.INSPECTOR);

  const getContent = (field: WorkOrderField) => {
    const isFieldEditDisabled = doesNotHaveRolePermissions(ROLE_SETS.INSPECTOR);

    const props = {
      field,
      onChange: handleChange,
      isDisabled: isFieldEditDisabled,
      onBlur: handleChange,
      customFieldView: CustomFieldView.DETAIL,
    };

    const hasSubFields = !isNilOrEmpty(field.subFields);

    const components = {
      [DataType.SUMMARY]: <WorkOrderSummary {...props} />,
      [DataType.SHORT_TEXT]: (
        <WorkOrderTextField {...props} onChange={handleChangeDebounced} />
      ),
      [DataType.LONG_TEXT]: (
        <WorkOrderTextField {...props} onChange={handleChangeDebounced} />
      ),
      [DataType.DATE]: (
        <WorkOrderDatePicker {...props} onChange={handleChangeDebounced} />
      ),
      [DataType.DATE_TIME]: (
        <WorkOrderDatePicker {...props} onChange={handleChangeDebounced} />
      ),
      [DataType.ENUM_SINGLE]: hasSubFields ? (
        <div className={classes.enumSingleNestedContainer}>
          <WorkOrderEnumSingleField
            {...props}
            onChange={handleChangeDebounced}
          />
        </div>
      ) : (
        <WorkOrderEnumSingleField {...props} onChange={handleChangeDebounced} />
      ),
      [DataType.ENUM_MULTIPLE]: <WorkOrderEnumMultipleField {...props} />,
      [DataType.NUMBER]: (
        <WorkOrderNumericField {...props} onChange={handleChangeDebounced} />
      ),
    };

    return (
      <div className={classes.fieldContainer}>{components[field.dataType]}</div>
    );
  };

  const hideSection = !showButton && isNilOrEmpty(fields);
  return (
    <>
      <div className={classes.sectionHeader}>Custom Fields Section</div>
      <div
        className={clsx(classes.sectionContent, hideSection && classes.hide)}
      >
        {fieldOrder?.length === 0 && (
          <div className={classes.emptyList}>There are no custom fields.</div>
        )}
        {fieldOrder?.map((fieldId: string) => {
          const field = fields.find(item => item.id === fieldId);
          return getContent(field);
        })}
      </div>
    </>
  );
};

export default CustomFields;
